<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';

const store = useStore();
const { accountScopedRoute } = useAccount();
const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const conversations = useMapGetter('getAllConversations');
const notifications = useMapGetter('notifications/getNotifications');
const unreadNotifications = useMapGetter('notifications/getUnreadCount');
const inboxes = useMapGetter('inboxes/getInboxes');
const agents = useMapGetter('agents/getAgents');
const agentStatus = useMapGetter('agents/getAgentStatus');
const teams = useMapGetter('teams/getTeams');

const firstName = computed(() => {
  const name =
    currentUser.value?.name ||
    currentUser.value?.display_name ||
    currentUser.value?.email?.split('@')?.[0] ||
    currentUser.value?.available_name ||
    'Usuario';
  return name.split(' ')[0];
});

const openConversations = computed(() =>
  conversations.value.filter(conversation => conversation.status === 'open')
);

const pendingConversations = computed(() =>
  openConversations.value.filter(conversation => !conversation.meta?.assignee)
);

const unattendedConversations = computed(() =>
  openConversations.value.filter(conversation => {
    const lastActivity =
      conversation.timestamp || conversation.last_activity_at;
    if (!lastActivity) return false;
    const lastActivityMs =
      String(lastActivity).length === 10 ? lastActivity * 1000 : lastActivity;
    return Date.now() - Number(lastActivityMs) > 15 * 60 * 1000;
  })
);

const mineConversations = computed(() =>
  openConversations.value.filter(
    conversation =>
      conversation.meta?.assignee?.id &&
      conversation.meta.assignee.id === currentUser.value?.id
  )
);

const inboxChannelCounts = computed(() => {
  return inboxes.value.reduce((acc, inbox) => {
    const type = inbox.channel_type || 'Canal';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});
});

const channelRows = computed(() =>
  Object.entries(inboxChannelCounts.value).map(([name, count]) => ({
    name: name.replace('Channel::', ''),
    count,
  }))
);

const recentConversations = computed(() => openConversations.value.slice(0, 5));
const recentNotifications = computed(() => notifications.value.slice(0, 5));
const connectedTeams = computed(() =>
  teams.value.filter(team => team.is_member || team.allow_auto_assign)
);

const toMilliseconds = value => {
  if (!value) return 0;
  const numeric = Number(value);
  if (!Number.isNaN(numeric)) {
    return String(value).length === 10 ? numeric * 1000 : numeric;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const latestMessage = conversation => {
  const messages = conversation.messages || [];
  return messages[messages.length - 1] || {};
};

const hasAgentReply = conversation => {
  return (conversation.messages || []).some(message => {
    return message.message_type === 1 || message.message_type === 'outgoing';
  });
};

const trafficStats = computed(() => {
  const arrived = openConversations.value.length;
  const responded = openConversations.value.filter(hasAgentReply).length;
  const unread = openConversations.value.filter(
    conversation => Number(conversation.unread_count || 0) > 0
  ).length;
  const responseRate = arrived ? Math.round((responded / arrived) * 100) : 0;

  return [
    {
      label: t('DASHBOARD_HOME.TRAFFIC.ARRIVED'),
      value: arrived,
      tone: 'cyan',
    },
    {
      label: t('DASHBOARD_HOME.TRAFFIC.RESPONDED'),
      value: responded,
      tone: 'green',
    },
    {
      label: t('DASHBOARD_HOME.TRAFFIC.UNREAD'),
      value: unread,
      tone: 'pink',
    },
    {
      label: t('DASHBOARD_HOME.TRAFFIC.RESPONSE_RATE'),
      value: `${responseRate}%`,
      tone: 'purple',
    },
  ];
});

const trafficBars = computed(() => {
  const now = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const days = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(now.getTime() - (6 - index) * dayMs);
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start.getTime() + dayMs);
    const dayConversations = openConversations.value.filter(conversation => {
      const activityAt = toMilliseconds(
        conversation.created_at ||
          conversation.timestamp ||
          conversation.last_activity_at
      );
      return activityAt >= start.getTime() && activityAt < end.getTime();
    });

    return {
      id: start.toISOString(),
      label: start.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: '2-digit',
      }),
      arrived: dayConversations.length,
      responded: dayConversations.filter(hasAgentReply).length,
    };
  });
  const maxValue = Math.max(
    ...days.map(day => Math.max(day.arrived, day.responded)),
    1
  );

  return days.map(day => ({
    ...day,
    arrivedHeight: Math.max(
      (day.arrived / maxValue) * 100,
      day.arrived ? 12 : 3
    ),
    respondedHeight: Math.max(
      (day.responded / maxValue) * 100,
      day.responded ? 12 : 3
    ),
  }));
});

const metrics = computed(() => [
  {
    label: t('DASHBOARD_HOME.METRICS.OPEN'),
    value: openConversations.value.length,
    tone: 'cyan',
  },
  {
    label: t('DASHBOARD_HOME.METRICS.UNATTENDED'),
    value: unattendedConversations.value.length,
    tone: 'pink',
  },
  {
    label: t('DASHBOARD_HOME.METRICS.UNASSIGNED'),
    value: pendingConversations.value.length,
    tone: 'purple',
  },
  {
    label: t('DASHBOARD_HOME.METRICS.MINE'),
    value: mineConversations.value.length,
    tone: 'white',
  },
]);

const agentRows = computed(() =>
  agents.value.slice(0, 6).map(agent => ({
    id: agent.id,
    name: agent.name || agent.available_name || agent.email,
    email: agent.email,
    status: agent.availability_status || 'offline',
  }))
);

onMounted(() => {
  store.dispatch('fetchAllConversations');
  store.dispatch('notifications/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
  store.dispatch('teams/get');
});
</script>

<template>
  <section class="tic-home">
    <header class="tic-home__hero">
      <div>
        <h1>{{ t('DASHBOARD_HOME.GREETING', { name: firstName }) }}</h1>
        <p>
          {{ t('DASHBOARD_HOME.SUMMARY_PREFIX') }}
          <strong>{{ unattendedConversations.length }}</strong>
          {{ t('DASHBOARD_HOME.SUMMARY_CHATS') }}
          <strong>{{ unreadNotifications || 0 }}</strong>
          {{ t('DASHBOARD_HOME.SUMMARY_NOTIFICATIONS') }}
        </p>
      </div>
      <RouterLink
        :to="accountScopedRoute('account_overview_reports')"
        class="tic-home__action"
      >
        <span class="i-lucide-chart-spline" />
        {{ t('DASHBOARD_HOME.VIEW_REPORTS') }}
      </RouterLink>
    </header>

    <div class="tic-home__grid">
      <article class="tic-home__card tic-home__card--wide">
        <div class="tic-home__card-title">
          <h2>{{ t('DASHBOARD_HOME.OPEN_CONVERSATIONS') }}</h2>
          <span class="tic-live-dot" />
          <span>{{ t('DASHBOARD_HOME.LIVE') }}</span>
        </div>
        <div class="tic-home__metrics">
          <div
            v-for="metric in metrics"
            :key="metric.label"
            class="tic-home__metric"
            :class="`is-${metric.tone}`"
          >
            <span>{{ metric.label }}</span>
            <strong>{{ metric.value }}</strong>
          </div>
        </div>
      </article>

      <article class="tic-home__card tic-home__card--list">
        <div class="tic-home__card-title">
          <h2>{{ t('DASHBOARD_HOME.AGENT_STATUS') }}</h2>
          <span class="tic-live-dot" />
        </div>
        <div class="tic-home__agent-status">
          <div>
            <span>{{ t('DASHBOARD_HOME.STATUS.ONLINE') }}</span>
            <strong>{{ agentStatus.online || 0 }}</strong>
          </div>
          <div>
            <span>{{ t('DASHBOARD_HOME.STATUS.BUSY') }}</span>
            <strong>{{ agentStatus.busy || 0 }}</strong>
          </div>
          <div>
            <span>{{ t('DASHBOARD_HOME.STATUS.OFFLINE') }}</span>
            <strong>{{ agentStatus.offline || 0 }}</strong>
          </div>
        </div>
      </article>

      <article class="tic-home__card">
        <div class="tic-home__card-title">
          <h2>{{ t('DASHBOARD_HOME.UNREAD_URGENT') }}</h2>
        </div>
        <div class="tic-home__list">
          <RouterLink
            v-for="conversation in recentConversations"
            :key="conversation.id"
            :to="
              accountScopedRoute('inbox_conversation', {
                conversation_id: conversation.id,
              })
            "
            class="tic-home__conversation"
          >
            <span class="tic-home__avatar">
              {{ conversation.meta?.sender?.name?.charAt(0) || '#' }}
            </span>
            <span>
              <strong>{{
                conversation.meta?.sender?.name || t('DASHBOARD_HOME.CONTACT')
              }}</strong>
              <small>{{
                conversation.messages?.[conversation.messages.length - 1]
                  ?.content ||
                latestMessage(conversation).content ||
                t('DASHBOARD_HOME.NO_PREVIEW')
              }}</small>
            </span>
          </RouterLink>
          <p v-if="!recentConversations.length" class="tic-home__empty">
            {{ t('DASHBOARD_HOME.NO_OPEN_CONVERSATIONS') }}
          </p>
        </div>
      </article>

      <article class="tic-home__card tic-home__card--wide">
        <div class="tic-home__card-title">
          <h2>{{ t('DASHBOARD_HOME.CONVERSATION_TRAFFIC') }}</h2>
          <span class="tic-live-dot" />
        </div>
        <div class="tic-home__traffic-summary">
          <div
            v-for="stat in trafficStats"
            :key="stat.label"
            :class="`is-${stat.tone}`"
          >
            <span>{{ stat.label }}</span>
            <strong>{{ stat.value }}</strong>
          </div>
        </div>
        <div class="tic-home__chart">
          <div v-for="day in trafficBars" :key="day.id" class="tic-home__day">
            <div class="tic-home__day-bars">
              <span
                class="is-arrived"
                :style="{ height: `${day.arrivedHeight}%` }"
                :title="
                  t('DASHBOARD_HOME.TRAFFIC.ARRIVED_TITLE', {
                    count: day.arrived,
                  })
                "
              />
              <span
                class="is-responded"
                :style="{ height: `${day.respondedHeight}%` }"
                :title="
                  t('DASHBOARD_HOME.TRAFFIC.RESPONDED_TITLE', {
                    count: day.responded,
                  })
                "
              />
            </div>
            <small>{{ day.label }}</small>
          </div>
        </div>
        <div class="tic-home__legend">
          <span>
            <i class="is-arrived" />{{ t('DASHBOARD_HOME.TRAFFIC.ARRIVED') }}
          </span>
          <span>
            <i class="is-responded" />{{
              t('DASHBOARD_HOME.TRAFFIC.RESPONDED')
            }}
          </span>
        </div>
      </article>

      <article class="tic-home__card tic-home__card--wide">
        <div class="tic-home__card-title">
          <h2>{{ t('DASHBOARD_HOME.NOTIFICATIONS') }}</h2>
        </div>
        <div class="tic-home__list">
          <div
            v-for="notification in recentNotifications"
            :key="notification.id"
            class="tic-home__notification"
          >
            <span class="i-lucide-bell" />
            <p>
              {{
                notification.message ||
                notification.primary_actor?.name ||
                t('DASHBOARD_HOME.RECENT_ACTIVITY')
              }}
            </p>
          </div>
          <p v-if="!recentNotifications.length" class="tic-home__empty">
            {{ t('DASHBOARD_HOME.NO_RECENT_NOTIFICATIONS') }}
          </p>
        </div>
      </article>

      <article class="tic-home__card">
        <div class="tic-home__card-title">
          <h2>{{ t('DASHBOARD_HOME.CONNECTED_AGENTS') }}</h2>
        </div>
        <div class="tic-home__table">
          <div v-for="agent in agentRows" :key="agent.id">
            <span class="tic-home__agent-dot" :class="`is-${agent.status}`" />
            <strong>{{ agent.name }}</strong>
            <small>{{ agent.status }}</small>
          </div>
          <p v-if="!agentRows.length" class="tic-home__empty">
            {{ t('DASHBOARD_HOME.NO_AGENTS') }}
          </p>
        </div>
      </article>

      <article class="tic-home__card">
        <div class="tic-home__card-title">
          <h2>{{ t('DASHBOARD_HOME.TEAMS_CHANNELS') }}</h2>
        </div>
        <div class="tic-home__chips">
          <span v-for="team in connectedTeams" :key="team.id">
            {{ team.name }}
          </span>
          <span v-for="channel in channelRows" :key="channel.name">
            {{ channel.name }} {{ channel.count }}
          </span>
          <p
            v-if="!connectedTeams.length && !channelRows.length"
            class="tic-home__empty"
          >
            {{ t('DASHBOARD_HOME.NO_TEAMS_CHANNELS') }}
          </p>
        </div>
      </article>
    </div>
  </section>
</template>

<style scoped lang="scss">
.tic-home {
  width: 100%;
  min-height: 100%;
  overflow: visible;
  padding: 1.5rem clamp(1rem, 2vw, 2rem) 3rem;
  color: rgba(248, 250, 252, 0.96);
}

.tic-home__hero {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1.5rem;

  h1 {
    margin: 0 0 0.35rem;
    font-size: 1.65rem;
    font-weight: 850;
  }

  p {
    max-width: 48rem;
    color: rgba(226, 232, 240, 0.78);
  }

  strong {
    color: #ff4d8d;
  }
}

.tic-home__action {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 2.55rem;
  padding: 0 1rem;
  border: 1px solid rgba(109, 40, 217, 0.8);
  border-radius: 0.85rem;
  background: linear-gradient(135deg, #7c3aed, #0284c7);
  box-shadow: 0 0.8rem 1.8rem rgba(124, 58, 237, 0.22);
  color: #fff;
  font-weight: 800;
}

.tic-home__grid {
  display: grid;
  grid-template-columns: minmax(18rem, 1fr) minmax(18rem, 1fr) minmax(
      18rem,
      1fr
    );
  gap: 1.25rem;
  align-items: start;
}

.tic-home__card {
  min-width: 0;
  min-height: 12rem;
  padding: 1.35rem;
  border: 1px solid rgba(148, 234, 255, 0.16);
  border-radius: 1.35rem;
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.055), transparent 46%),
    rgba(5, 8, 18, 0.48);
  box-shadow: 0 1rem 2.5rem rgba(0, 0, 0, 0.22);
  overflow: hidden;
}

.tic-home__card--wide {
  grid-column: span 2;
}

.tic-home__card--list {
  display: flex;
  flex-direction: column;
  max-height: 27.5rem;
}

.tic-home__card--list .tic-home__list {
  min-height: 0;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.tic-home__card-title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(148, 234, 255, 0.12);

  h2 {
    margin: 0;
    font-size: 1rem;
    font-weight: 850;
  }

  span:last-child {
    color: #2fffd2;
    font-size: 0.75rem;
    font-weight: 900;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
}

.tic-live-dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: #2fffd2;
  box-shadow: 0 0 0.7rem #2fffd2;
}

.tic-home__metrics,
.tic-home__agent-status {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.tic-home__agent-status {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.tic-home__metric,
.tic-home__agent-status div {
  display: grid;
  place-items: center;
  min-height: 5.25rem;
  border: 1px solid rgba(148, 234, 255, 0.12);
  border-radius: 0.9rem;
  background: rgba(255, 255, 255, 0.035);

  span {
    color: #cbd5e1;
    font-size: 0.75rem;
    font-weight: 850;
  }

  strong {
    font-size: 2rem;
    line-height: 1;
  }
}

.is-cyan strong {
  color: #26d9f7;
}

.is-pink strong {
  color: #ff4d8d;
}

.is-purple strong {
  color: #a855f7;
}

.tic-home__list,
.tic-home__table,
.tic-home__chips {
  display: grid;
  gap: 0.8rem;
  margin-top: 1rem;
}

.tic-home__conversation,
.tic-home__notification,
.tic-home__table div {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  min-height: 3.5rem;
  padding: 0.75rem;
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.035);
  color: inherit;
  min-width: 0;
  width: 100%;
  overflow: hidden;
}

.tic-home__avatar {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 0.8rem;
  background: linear-gradient(135deg, #8b2ac9, #26d9f7);
  font-weight: 900;
}

.tic-home__conversation span:last-child {
  min-width: 0;
}

.tic-home__conversation strong,
.tic-home__conversation small,
.tic-home__table strong,
.tic-home__table small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tic-home__conversation small,
.tic-home__table small,
.tic-home__empty {
  color: rgba(226, 232, 240, 0.68);
}

.tic-home__conversation small,
.tic-home__table small {
  font-size: max(0.8125rem, 13px);
  line-height: 1.35;
}

.tic-home__chart {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 0.7rem;
  height: 10rem;
  margin-top: 0.85rem;
  padding: 1rem;
  border: 1px dashed rgba(38, 217, 247, 0.18);
  border-radius: 1rem;
  background: rgba(5, 8, 18, 0.26);
  overflow-x: auto;
}

.tic-home__traffic-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;

  div {
    min-width: 0;
    padding: 0.8rem;
    border: 1px solid rgba(148, 234, 255, 0.12);
    border-radius: 0.85rem;
    background: rgba(255, 255, 255, 0.035);
  }

  span {
    display: block;
    color: #cbd5e1;
    font-size: 0.74rem;
    font-weight: 750;
  }

  strong {
    display: block;
    margin-top: 0.25rem;
    font-size: 1.65rem;
    line-height: 1;
  }
}

.tic-home__day {
  display: grid;
  grid-template-rows: 1fr auto;
  justify-items: center;
  gap: 0.45rem;
  flex: 1 1 0;
  min-width: 4.25rem;

  small {
    color: #cbd5e1;
    font-size: 0.75rem;
    font-weight: 800;
    line-height: 1.15;
    text-align: center;
    text-transform: capitalize;
    white-space: normal;
  }
}

.tic-home__day-bars {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 0.25rem;
  width: 100%;
  min-height: 0;

  span {
    width: min(1.5rem, 42%);
    border-radius: 0.3rem 0.3rem 0 0;
    transition: height 0.18s ease;
  }
}

.is-arrived {
  background: #26d9f7;
}

.is-responded {
  background: #7c2dbc;
}

.tic-home__legend {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-top: 0.75rem;
  color: #475569;
  font-size: 0.78rem;
  font-weight: 800;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  i {
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 0.2rem;
  }
}

.tic-home__agent-dot {
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: #94a3b8;
}

.tic-home__agent-dot.is-online {
  background: #2fffd2;
  box-shadow: 0 0 0.7rem #2fffd2;
}

.tic-home__agent-dot.is-busy {
  background: #fbbf24;
}

.tic-home__chips {
  display: flex;
  flex-wrap: wrap;

  span {
    padding: 0.55rem 0.75rem;
    border: 1px solid rgba(148, 234, 255, 0.14);
    border-radius: 999px;
    background: rgba(38, 217, 247, 0.1);
    color: rgba(248, 250, 252, 0.92);
    font-weight: 800;
  }
}

@media (max-width: 1180px) {
  .tic-home__grid {
    grid-template-columns: 1fr;
  }

  .tic-home__card--wide {
    grid-column: auto;
  }
}

@media (max-width: 720px) {
  .tic-home {
    padding: 1rem;
  }

  .tic-home__hero {
    flex-direction: column;
  }

  .tic-home__metrics,
  .tic-home__agent-status {
    grid-template-columns: 1fr 1fr;
  }

  .tic-home__traffic-summary {
    grid-template-columns: 1fr 1fr;
  }
}

:global(body:not(.dark)) .tic-home {
  color: rgba(8, 17, 31, 0.96);
}

:global(body:not(.dark)) .tic-home__hero p,
:global(body:not(.dark)) .tic-home__conversation small,
:global(body:not(.dark)) .tic-home__table small,
:global(body:not(.dark)) .tic-home__empty {
  color: rgba(15, 23, 42, 0.78);
}

:global(body:not(.dark)) .tic-home__card {
  background: rgba(255, 255, 255, 0.86);
  border-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 12px 32px 0 rgba(31, 38, 135, 0.1);
  backdrop-filter: blur(20px);
}

:global(body:not(.dark)) .tic-home__conversation,
:global(body:not(.dark)) .tic-home__notification,
:global(body:not(.dark)) .tic-home__table div,
:global(body:not(.dark)) .tic-home__metric,
:global(body:not(.dark)) .tic-home__traffic-summary div,
:global(body:not(.dark)) .tic-home__agent-status div {
  background: rgba(248, 250, 252, 0.92);
  border-color: rgba(15, 23, 42, 0.08);
}

:global(body:not(.dark)) .tic-home__chart {
  background: linear-gradient(
    180deg,
    rgba(38, 217, 247, 0.05) 0%,
    transparent 100%
  );
  border-color: rgba(0, 0, 0, 0.08);
}

:global(body:not(.dark)) .tic-home__conversation,
:global(body:not(.dark)) .tic-home__notification,
:global(body:not(.dark)) .tic-home__table div {
  background: rgba(248, 250, 252, 0.96);
}

:global(body:not(.dark)) .tic-home__traffic-summary span,
:global(body:not(.dark)) .tic-home__day small,
:global(body:not(.dark)) .tic-home__legend {
  color: rgba(51, 65, 85, 0.9);
}

:global(body:not(.dark)) .tic-home__card-title {
  border-bottom-color: rgba(0, 0, 0, 0.04);
}

:global(body:not(.dark)) .tic-home__metric span,
:global(body:not(.dark)) .tic-home__agent-status span {
  color: rgba(51, 65, 85, 0.9);
}

:global(body:not(.dark)) .is-cyan strong,
:global(html:not(.dark)) .is-cyan strong,
:global(.light) .is-cyan strong {
  color: #0369a1;
}

:global(body:not(.dark)) .is-pink strong,
:global(html:not(.dark)) .is-pink strong,
:global(.light) .is-pink strong {
  color: #be185d;
}

:global(body:not(.dark)) .is-purple strong,
:global(html:not(.dark)) .is-purple strong,
:global(.light) .is-purple strong {
  color: #7e22ce;
}

:global(body:not(.dark)) .tic-home__chips span {
  border-color: rgba(14, 165, 233, 0.22);
  background: rgba(38, 217, 247, 0.16);
  color: #075985;
}

:global(html:not(.dark)) .tic-home__metric span,
:global(html:not(.dark)) .tic-home__agent-status span,
:global(html:not(.dark)) .tic-home__traffic-summary span,
:global(html:not(.dark)) .tic-home__day small,
:global(html:not(.dark)) .tic-home__legend,
:global(.light) .tic-home__metric span,
:global(.light) .tic-home__agent-status span,
:global(.light) .tic-home__traffic-summary span,
:global(.light) .tic-home__day small,
:global(.light) .tic-home__legend {
  color: #334155;
}

:global(html:not(.dark)) .tic-home__conversation small,
:global(html:not(.dark)) .tic-home__table small,
:global(.light) .tic-home__conversation small,
:global(.light) .tic-home__table small {
  color: #475569;
}

:global(html:not(.dark)) .tic-home__chips span,
:global(.light) .tic-home__chips span {
  border-color: rgba(14, 165, 233, 0.28);
  background: rgba(14, 165, 233, 0.12);
  color: #075985;
}

:global(.dark) .tic-home__metric span,
:global(.dark) .tic-home__agent-status span,
:global(.dark) .tic-home__traffic-summary span,
:global(.dark) .tic-home__day small,
:global(.dark) .tic-home__legend {
  color: rgba(203, 213, 225, 0.88);
}
</style>
